import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Copy, Eye, EyeOff, Trash2, RefreshCw, Code, ExternalLink, Key, Activity, Settings } from "lucide-react";
import { generateStrongPassword } from "@/lib/security";
import { safeRandomUUID } from "@/utils/id";

interface DeveloperApp {
  id: string;
  name: string;
  description: string | null;
  api_key: string;
  api_secret: string;
  domain_whitelist: string[];
  is_active: boolean;
  rate_limit_per_minute: number;
  total_requests: number;
  created_at: string;
  updated_at: string;
}

interface ApiUsageLog {
  id: string;
  endpoint: string;
  method: string;
  status_code: number;
  request_ip: string | null;
  created_at: string;
}

export default function DeveloperApps() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [apps, setApps] = useState<DeveloperApp[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState<DeveloperApp | null>(null);
  const [usageLogs, setUsageLogs] = useState<ApiUsageLog[]>([]);
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});
  
  // Form states
  const [appName, setAppName] = useState("");
  const [appDescription, setAppDescription] = useState("");
  const [domainWhitelist, setDomainWhitelist] = useState("");

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      fetchApps();
    }
  }, [user]);

  const fetchApps = async () => {
    try {
      const { data, error } = await supabase
        .from("developer_apps")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setApps((data as unknown as DeveloperApp[]) || []);
    } catch (error) {
      console.error("Error fetching apps:", error);
      toast.error("Failed to load apps");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsageLogs = async (appId: string) => {
    try {
      const { data, error } = await supabase
        .from("api_usage_logs")
        .select("*")
        .eq("app_id", appId)
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;
      setUsageLogs((data as unknown as ApiUsageLog[]) || []);
    } catch (error) {
      console.error("Error fetching usage logs:", error);
    }
  };

  const createApp = async () => {
    if (!appName.trim()) {
      toast.error("App name is required");
      return;
    }

    try {
      const apiSecret = generateStrongPassword(32);
      const domains = domainWhitelist.split(",").map(d => d.trim()).filter(d => d);

      const { data, error } = await supabase
        .from("developer_apps")
        .insert({
          user_id: user!.id,
          name: appName.trim(),
          description: appDescription.trim() || null,
          api_secret: apiSecret,
          domain_whitelist: domains,
        })
        .select()
        .single();

      if (error) throw error;

      setApps([data as unknown as DeveloperApp, ...apps]);
      setCreateDialogOpen(false);
      setAppName("");
      setAppDescription("");
      setDomainWhitelist("");
      toast.success("App created successfully");
    } catch (error) {
      console.error("Error creating app:", error);
      toast.error("Failed to create app");
    }
  };

  const toggleAppStatus = async (app: DeveloperApp) => {
    try {
      const { error } = await supabase
        .from("developer_apps")
        .update({ is_active: !app.is_active })
        .eq("id", app.id);

      if (error) throw error;

      setApps(apps.map(a => a.id === app.id ? { ...a, is_active: !a.is_active } : a));
      toast.success(`App ${app.is_active ? "disabled" : "enabled"}`);
    } catch (error) {
      console.error("Error toggling app status:", error);
      toast.error("Failed to update app");
    }
  };

  const regenerateApiKey = async (app: DeveloperApp) => {
    try {
      const newSecret = generateStrongPassword(32);
      const { data, error } = await supabase
        .from("developer_apps")
        .update({ 
          api_key: safeRandomUUID(),
          api_secret: newSecret 
        })
        .eq("id", app.id)
        .select()
        .single();

      if (error) throw error;

      setApps(apps.map(a => a.id === app.id ? data as unknown as DeveloperApp : a));
      toast.success("API key regenerated");
    } catch (error) {
      console.error("Error regenerating API key:", error);
      toast.error("Failed to regenerate API key");
    }
  };

  const deleteApp = async (app: DeveloperApp) => {
    try {
      const { error } = await supabase
        .from("developer_apps")
        .delete()
        .eq("id", app.id);

      if (error) throw error;

      setApps(apps.filter(a => a.id !== app.id));
      setSelectedApp(null);
      toast.success("App deleted");
    } catch (error) {
      console.error("Error deleting app:", error);
      toast.error("Failed to delete app");
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="marketing-page min-h-screen bg-background">
      <Navigation />
      <main className="container mx-auto px-4 py-8 pt-24">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">{t("developerApps.title", "Developer Apps")}</h1>
            <p className="text-muted-foreground mt-1">
              {t("developerApps.subtitle", "Create and manage API keys to integrate MeetHub with your platform")}
            </p>
          </div>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                {t("developerApps.createApp", "Create App")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t("developerApps.createNewApp", "Create New App")}</DialogTitle>
                <DialogDescription>
                  {t("developerApps.createDescription", "Create an app to get API credentials for integrating MeetHub meetings into your platform.")}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="appName">{t("developerApps.appName", "App Name")} *</Label>
                  <Input
                    id="appName"
                    value={appName}
                    onChange={(e) => setAppName(e.target.value)}
                    placeholder="My Integration"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="appDescription">{t("developerApps.description", "Description")}</Label>
                  <Textarea
                    id="appDescription"
                    value={appDescription}
                    onChange={(e) => setAppDescription(e.target.value)}
                    placeholder="Brief description of your integration"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="domains">{t("developerApps.domainWhitelist", "Domain Whitelist (optional)")}</Label>
                  <Input
                    id="domains"
                    value={domainWhitelist}
                    onChange={(e) => setDomainWhitelist(e.target.value)}
                    placeholder="example.com, app.example.com"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t("developerApps.domainHint", "Comma-separated domains. Leave empty to allow all domains.")}
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                  {t("common.cancel", "Cancel")}
                </Button>
                <Button onClick={createApp}>
                  {t("developerApps.create", "Create")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {apps.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Key className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t("developerApps.noApps", "No Apps Yet")}</h3>
              <p className="text-muted-foreground mb-4">
                {t("developerApps.noAppsDescription", "Create your first app to start integrating MeetHub meetings into your platform.")}
              </p>
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                {t("developerApps.createFirstApp", "Create Your First App")}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Apps List */}
            <div className="lg:col-span-1 space-y-4">
              {apps.map((app) => (
                <Card 
                  key={app.id} 
                  className={`cursor-pointer transition-colors hover:border-primary ${selectedApp?.id === app.id ? "border-primary" : ""}`}
                  onClick={() => {
                    setSelectedApp(app);
                    fetchUsageLogs(app.id);
                  }}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">{app.name}</CardTitle>
                      <Badge variant={app.is_active ? "default" : "secondary"}>
                        {app.is_active ? t("developerApps.active", "Active") : t("developerApps.inactive", "Inactive")}
                      </Badge>
                    </div>
                    {app.description && (
                      <CardDescription>{app.description}</CardDescription>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <span>{t("developerApps.requests", "Requests")}: {app.total_requests}</span>
                      <span>{new Date(app.created_at).toLocaleDateString()}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* App Details */}
            <div className="lg:col-span-2">
              {selectedApp ? (
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>{selectedApp.name}</CardTitle>
                        <CardDescription>{selectedApp.description}</CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={selectedApp.is_active}
                          onCheckedChange={() => toggleAppStatus(selectedApp)}
                        />
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="destructive" size="icon">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>{t("developerApps.deleteConfirm", "Delete App?")}</AlertDialogTitle>
                              <AlertDialogDescription>
                                {t("developerApps.deleteWarning", "This will permanently delete this app and revoke its API key. This action cannot be undone.")}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>{t("common.cancel", "Cancel")}</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteApp(selectedApp)}>
                                {t("common.delete", "Delete")}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Tabs defaultValue="credentials">
                      <TabsList className="mb-4">
                        <TabsTrigger value="credentials">
                          <Key className="h-4 w-4 mr-2" />
                          {t("developerApps.credentials", "Credentials")}
                        </TabsTrigger>
                        <TabsTrigger value="usage">
                          <Activity className="h-4 w-4 mr-2" />
                          {t("developerApps.usage", "Usage")}
                        </TabsTrigger>
                        <TabsTrigger value="docs">
                          <Code className="h-4 w-4 mr-2" />
                          {t("developerApps.docs", "API Docs")}
                        </TabsTrigger>
                      </TabsList>

                      <TabsContent value="credentials" className="space-y-4">
                        <div className="space-y-2">
                          <Label>{t("developerApps.apiKey", "API Key")}</Label>
                          <div className="flex gap-2">
                            <Input
                              value={showSecrets[selectedApp.id] ? selectedApp.api_key : "â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢-â€¢â€¢â€¢â€¢-â€¢â€¢â€¢â€¢-â€¢â€¢â€¢â€¢-â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢"}
                              readOnly
                              className="font-mono"
                            />
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => setShowSecrets({ ...showSecrets, [selectedApp.id]: !showSecrets[selectedApp.id] })}
                            >
                              {showSecrets[selectedApp.id] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </Button>
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => copyToClipboard(selectedApp.api_key, "API Key")}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline">
                                <RefreshCw className="h-4 w-4 mr-2" />
                                {t("developerApps.regenerate", "Regenerate Key")}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>{t("developerApps.regenerateConfirm", "Regenerate API Key?")}</AlertDialogTitle>
                                <AlertDialogDescription>
                                  {t("developerApps.regenerateWarning", "This will invalidate the current API key. Any integrations using the old key will stop working.")}
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>{t("common.cancel", "Cancel")}</AlertDialogCancel>
                                <AlertDialogAction onClick={() => regenerateApiKey(selectedApp)}>
                                  {t("developerApps.regenerate", "Regenerate")}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>

                        <div className="pt-4 border-t">
                          <h4 className="font-medium mb-2">{t("developerApps.settings", "Settings")}</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">{t("developerApps.rateLimit", "Rate Limit")}:</span>
                              <span>{selectedApp.rate_limit_per_minute} {t("developerApps.perMinute", "per minute")}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">{t("developerApps.domains", "Allowed Domains")}:</span>
                              <span>{selectedApp.domain_whitelist?.length > 0 ? selectedApp.domain_whitelist.join(", ") : t("developerApps.allDomains", "All domains")}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">{t("developerApps.totalRequests", "Total Requests")}:</span>
                              <span>{selectedApp.total_requests}</span>
                            </div>
                          </div>
                        </div>
                      </TabsContent>

                      <TabsContent value="usage">
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium">{t("developerApps.recentRequests", "Recent Requests")}</h4>
                            <Button variant="outline" size="sm" onClick={() => fetchUsageLogs(selectedApp.id)}>
                              <RefreshCw className="h-4 w-4 mr-2" />
                              {t("common.refresh", "Refresh")}
                            </Button>
                          </div>
                          {usageLogs.length === 0 ? (
                            <p className="text-muted-foreground text-center py-8">
                              {t("developerApps.noRequests", "No API requests yet")}
                            </p>
                          ) : (
                            <div className="space-y-2 max-h-[400px] overflow-y-auto">
                              {usageLogs.map((log) => (
                                <div key={log.id} className="flex items-center justify-between p-2 bg-muted/50 rounded text-sm">
                                  <div className="flex items-center gap-3">
                                    <Badge variant={log.status_code < 400 ? "default" : "destructive"}>
                                      {log.status_code}
                                    </Badge>
                                    <span className="font-mono">{log.method}</span>
                                    <span className="text-muted-foreground">{log.endpoint}</span>
                                  </div>
                                  <span className="text-muted-foreground">
                                    {new Date(log.created_at).toLocaleString()}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </TabsContent>

                      <TabsContent value="docs">
                        <div className="space-y-6">
                          <div>
                            <h4 className="font-medium mb-2">{t("developerApps.apiEndpoint", "API Endpoint")}</h4>
                            <div className="flex gap-2">
                              <Input
                                value="https://wdshnonjtrfhreccaqul.supabase.co/functions/v1/create-meeting-api"
                                readOnly
                                className="font-mono text-sm"
                              />
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={() => copyToClipboard("https://wdshnonjtrfhreccaqul.supabase.co/functions/v1/create-meeting-api", "API Endpoint")}
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>

                          <div>
                            <h4 className="font-medium mb-2">{t("developerApps.exampleRequest", "Example Request")}</h4>
                            <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm">
{`curl -X POST \\
  https://wdshnonjtrfhreccaqul.supabase.co/functions/v1/create-meeting-api \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: ${selectedApp.api_key}" \\
  -d '{
    "title": "Team Standup",
    "description": "Daily team sync",
    "scheduled_start": "2024-01-20T10:00:00Z",
    "scheduled_end": "2024-01-20T10:30:00Z",
    "waiting_room_enabled": false,
    "max_participants": 50
  }'`}
                            </pre>
                          </div>

                          <div>
                            <h4 className="font-medium mb-2">{t("developerApps.responseExample", "Response Example")}</h4>
                            <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm">
{`{
  "success": true,
  "meeting": {
    "id": "uuid-here",
    "title": "Team Standup",
    "meeting_code": "ABCD-EFGH-IJKL",
    "join_url": "https://meethub-live.lovable.app/join/ABCD-EFGH-IJKL",
    "host_join_url": "https://meethub-live.lovable.app/join/ABCD-EFGH-IJKL?host=true",
    "created_at": "2024-01-19T12:00:00Z"
  }
}`}
                            </pre>
                          </div>

                          <div>
                            <h4 className="font-medium mb-2">{t("developerApps.requestParams", "Request Parameters")}</h4>
                            <div className="space-y-2 text-sm">
                              <div className="grid grid-cols-3 gap-2 p-2 bg-muted/50 rounded">
                                <span className="font-mono">title</span>
                                <span>string *</span>
                                <span className="text-muted-foreground">{t("developerApps.meetingTitle", "Meeting title")}</span>
                              </div>
                              <div className="grid grid-cols-3 gap-2 p-2 bg-muted/50 rounded">
                                <span className="font-mono">description</span>
                                <span>string</span>
                                <span className="text-muted-foreground">{t("developerApps.meetingDescription", "Meeting description")}</span>
                              </div>
                              <div className="grid grid-cols-3 gap-2 p-2 bg-muted/50 rounded">
                                <span className="font-mono">scheduled_start</span>
                                <span>ISO 8601</span>
                                <span className="text-muted-foreground">{t("developerApps.startTime", "Start time")}</span>
                              </div>
                              <div className="grid grid-cols-3 gap-2 p-2 bg-muted/50 rounded">
                                <span className="font-mono">scheduled_end</span>
                                <span>ISO 8601</span>
                                <span className="text-muted-foreground">{t("developerApps.endTime", "End time")}</span>
                              </div>
                              <div className="grid grid-cols-3 gap-2 p-2 bg-muted/50 rounded">
                                <span className="font-mono">waiting_room_enabled</span>
                                <span>boolean</span>
                                <span className="text-muted-foreground">{t("developerApps.waitingRoom", "Enable waiting room")}</span>
                              </div>
                              <div className="grid grid-cols-3 gap-2 p-2 bg-muted/50 rounded">
                                <span className="font-mono">password</span>
                                <span>string</span>
                                <span className="text-muted-foreground">{t("developerApps.meetingPassword", "Meeting password")}</span>
                              </div>
                              <div className="grid grid-cols-3 gap-2 p-2 bg-muted/50 rounded">
                                <span className="font-mono">max_participants</span>
                                <span>number</span>
                                <span className="text-muted-foreground">{t("developerApps.maxParticipants", "Max participants (default: 100)")}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </TabsContent>
                    </Tabs>
                  </CardContent>
                </Card>
              ) : (
                <Card className="flex items-center justify-center h-[400px]">
                  <CardContent className="text-center">
                    <Settings className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">
                      {t("developerApps.selectApp", "Select an app to view details")}
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}


